import {
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  Background,
  ReactFlow,
  ReactFlowProvider,
  useReactFlow,
  type EdgeChange,
  type NodeChange,
  type Connection,
  type Edge,
  type EdgeMarker,
  type NodeTypes,
} from '@xyflow/react'
import { MarkerType } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { memo, useCallback, useEffect, useMemo, useRef, useState, type DragEvent } from 'react'
import { toast } from 'sonner'

import { useOnViewportChange } from '@xyflow/react'
import CanvasActionsMenu from './canvas-actions-menu/CanvasActionsMenu'
import FloatingToolbar from './floating-toolbar/FloatingToolbar'
import { SIDEBAR_DRAG_MIME, type SidebarDragPayload } from '../sidebar/SidebarContent'
import styles from './Canvas.module.scss'
import { DestinationNode, IntegrationNode, SourceNode, TransformationNode } from './nodes'
import type {
  DestinationNode as DestinationPipelineNode,
  IntegrationNode as IntegrationPipelineNode,
  PipelineNode,
  SourceNode as SourcePipelineNode,
  TransformationNode as TransformationPipelineNode,
} from './types/canvas.types'
import { useAppDispatch, useAppSelector } from '@/app/reduxHooks'
import { throttle } from '@/features/adp/state/canvas/canvasSync.utils'
import {
  syncCanvasState,
  undo as undoAction,
  redo as redoAction,
  clearCanvas as clearCanvasAction,
} from '@/features/adp/state/canvas/canvasSlice'
import {
  selectCanvasNodes,
  selectCanvasEdges,
  selectCanUndo,
  selectCanRedo,
} from '@/features/adp/state/canvas/canvasSelectors'
import ValidationIssuesPanel from './validation/ValidationIssuesPanel'
import type { NodeValidationDecoration } from './types/canvas.types'
import type { ValidationResult } from '@/features/adp/utils/pipelineValidator'

const MULTIPLE_DESTINATIONS_NOT_ALLOWED_TOAST =
  'Multiple destinations are not supported. Please keep only one destination node.'

// ─── ZoomTracker ──────────────────────────────────────────────────────────────

function ZoomTracker({ onZoomPctChange }: { onZoomPctChange: (pct: number) => void }) {
  useOnViewportChange({
    onChange: viewport => {
      onZoomPctChange(Math.round(viewport.zoom * 100))
    },
  })
  return null
}

// ─── Pipeline graph (local nodes/edges) ───────────────────────────────────────
// Isolated so high-frequency node position updates during drag do not re-render
// the rest of the page (toolbar, menus). DevTools makes that extra work very noticeable.

type AdpReactFlowCanvasProps = {
  canvasLocked: boolean
  reduxNodes: PipelineNode[]
  reduxEdges: Edge[]
  onZoomPctChange: (pct: number) => void
  onHasCanvasNodesChange: (hasNodes: boolean) => void
}

const AdpReactFlowCanvas = memo(function AdpReactFlowCanvas({
  canvasLocked,
  reduxNodes,
  reduxEdges,
  onZoomPctChange,
  onHasCanvasNodesChange,
}: AdpReactFlowCanvasProps) {
  const nodeTypes = useMemo<NodeTypes>(
    () => ({
      source: SourceNode,
      destination: DestinationNode,
      transformation: TransformationNode,
      integration: IntegrationNode,
    }),
    []
  )

  const [nodes, setNodes] = useState<PipelineNode[]>([])
  const [edges, setEdges] = useState<Edge[]>([])

  const { fitView, screenToFlowPosition } = useReactFlow()

  const dispatch = useAppDispatch()
  const isApplyingReduxStateRef = useRef(false)

  useEffect(() => {
    isApplyingReduxStateRef.current = true
    setNodes(reduxNodes)
    setEdges(reduxEdges)
  }, [reduxNodes, reduxEdges])

  useEffect(() => {
    if (nodes.length === 1) {
      fitView({ padding: 0.2, maxZoom: 0.75 })
    }
  }, [nodes.length, fitView])

  useEffect(() => {
    onHasCanvasNodesChange(nodes.length > 0)
  }, [nodes.length, onHasCanvasNodesChange])

  const throttledSync = useRef(
    throttle((currentNodes: PipelineNode[], currentEdges: Edge[]) => {
      dispatch(syncCanvasState({ nodes: currentNodes, edges: currentEdges }))
    }, 200)
  ).current

  useEffect(() => {
    if (isApplyingReduxStateRef.current) {
      isApplyingReduxStateRef.current = false
      return
    }
    throttledSync(nodes, edges)
  }, [nodes, edges, throttledSync])

  const onNodesChangeInternal = useCallback((changes: NodeChange<PipelineNode>[]) => {
    setNodes(nds => applyNodeChanges(changes, nds) as PipelineNode[])
  }, [])

  const onEdgesChangeInternal = useCallback((changes: EdgeChange<Edge>[]) => {
    setEdges(eds => applyEdgeChanges(changes, eds))
  }, [])

  const onConnect = useCallback((params: Connection) => {
    setEdges(eds => addEdge(params, eds))
  }, [])

  const ensureEdge = useCallback(
    (sourceNodeId: string, targetNodeId: string) => {
      const edgeId = `e${sourceNodeId}-${targetNodeId}`
      setEdges(eds => {
        const exists = eds.some(
          e => e.id === edgeId || (e.source === sourceNodeId && e.target === targetNodeId)
        )
        return exists ? eds : eds.concat({ id: edgeId, source: sourceNodeId, target: targetNodeId })
      })
    },
    [setEdges]
  )

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault()

      const raw = event.dataTransfer.getData(SIDEBAR_DRAG_MIME)
      if (!raw) return

      let payload: SidebarDragPayload
      try {
        payload = JSON.parse(raw)
      } catch {
        return
      }

      const position = screenToFlowPosition({ x: event.clientX, y: event.clientY })
      const uuid = crypto.randomUUID()
      const nodeId = `${payload.nodeType}_${uuid}`

      const baseData = {
        id: nodeId,
        nodeTitle: payload.label,
        image: payload.imageLink ?? undefined,
        originalId: payload.originalId,
      }

      let newNode: PipelineNode

      switch (payload.nodeType) {
        case 'source': {
          const node: SourcePipelineNode = {
            id: nodeId,
            type: 'source',
            position,
            data: {
              ...baseData,
              mode: null,
              sourceType: payload.sourceType ?? '',
              source_uuid: uuid,
            },
          }
          newNode = node
          break
        }
        case 'destination': {
          const node: DestinationPipelineNode = {
            id: nodeId,
            type: 'destination',
            position,
            data: {
              ...baseData,
              mode: null,
              destinationType: payload.destinationType ?? '',
              destination_uuid: uuid,
            },
          }
          newNode = node
          break
        }
        case 'transformation': {
          const node: TransformationPipelineNode = {
            id: nodeId,
            type: 'transformation',
            position,
            data: {
              ...baseData,
              transformation_uuid: uuid,
            },
          }
          newNode = node
          break
        }
        case 'integration': {
          const node: IntegrationPipelineNode = {
            id: nodeId,
            type: 'integration',
            position,
            data: {
              ...baseData,
              integration_uuid: uuid,
              // Avoid `abbreviation: ''` (a stale catalog row could send one).
              // IntegrationNode treats undefined as legacy → Join fallback.
              abbreviation: payload.abbreviation || undefined,
            },
          }
          newNode = node
          break
        }
        default:
          return
      }

      setNodes(nds => {
        if (payload.nodeType === 'destination' && nds.some(node => node.type === 'destination')) {
          toast.error(MULTIPLE_DESTINATIONS_NOT_ALLOWED_TOAST)
          return nds
        }

        // Auto-connect: when dropping the 2nd node (transformation/destination)
        // and the only existing node is a source node, connect source -> newNode.
        const shouldAutoConnect =
          nds.length === 1 &&
          nds[0]?.type === 'source' &&
          (payload.nodeType === 'transformation' || payload.nodeType === 'destination')

        if (shouldAutoConnect) {
          const sourceNodeId = nds[0].id
          const targetNodeId = newNode.id
          ensureEdge(sourceNodeId, targetNodeId)
        }

        return nds.concat(newNode)
      })
    },
    [ensureEdge, screenToFlowPosition]
  )

  const hasNodes = nodes.length > 0

  return (
    <>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChangeInternal}
        onEdgesChange={onEdgesChangeInternal}
        onConnect={onConnect}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        nodeTypes={nodeTypes}
        minZoom={0.25}
        maxZoom={2}
        nodesDraggable={!canvasLocked}
        nodesConnectable={!canvasLocked}
        elementsSelectable={!canvasLocked}
        panOnDrag={!canvasLocked}
        zoomOnScroll={!canvasLocked}
        zoomOnPinch={!canvasLocked}
        zoomOnDoubleClick={!canvasLocked}
      >
        <Background gap={16} size={1} color="#cbd5e1" />
        <ZoomTracker onZoomPctChange={onZoomPctChange} />
      </ReactFlow>

      {!hasNodes && (
        <section className={styles.emptyOverlay} aria-label="Pipeline workspace">
          <div className={styles.emptyOverlayInner}>
            <p className={styles.emptyTitle}>New Project</p>
            <p className={styles.emptySub}>Drag and drop assets here to build a pipeline</p>
            <img className={styles.emptyIllustration} src="/pipeline-empty-state.png" alt="" />
          </div>
        </section>
      )}
    </>
  )
})

// ─── CanvasFlow ───────────────────────────────────────────────────────────────

function CanvasFlow() {
  const [zoomPct, setZoomPct] = useState(100)
  const [canvasLocked, setCanvasLocked] = useState(false)
  const [hasCanvasNodes, setHasCanvasNodes] = useState(false)

  const { fitView, getZoom, getViewport, setViewport, zoomIn, zoomOut } = useReactFlow()

  const dispatch = useAppDispatch()
  const reduxNodes = useAppSelector(selectCanvasNodes)
  const reduxEdges = useAppSelector(selectCanvasEdges)
  const canUndo = useAppSelector(selectCanUndo)
  const canRedo = useAppSelector(selectCanRedo)
  const validationResult = useAppSelector(state => state.validation.result)
  const isValidationDirty = useAppSelector(state => state.validation.isDirty)

  const decoratedNodes = useMemo(
    () => decorateNodesWithValidation(reduxNodes, validationResult, isValidationDirty),
    [reduxNodes, validationResult, isValidationDirty]
  )

  const decoratedEdges = useMemo(
    () => decorateEdgesWithValidation(reduxEdges, validationResult, isValidationDirty),
    [reduxEdges, validationResult, isValidationDirty]
  )

  const onHasCanvasNodesChange = useCallback((has: boolean) => {
    setHasCanvasNodes(prev => (prev === has ? prev : has))
  }, [])

  // ── Undo / Redo ──
  const handleUndo = useCallback(() => dispatch(undoAction()), [dispatch])
  const handleRedo = useCallback(() => dispatch(redoAction()), [dispatch])

  // ── Canvas controls ──
  const handleToggleLock = useCallback(() => setCanvasLocked(prev => !prev), [])

  const handleFit = useCallback(() => {
    if (canvasLocked) return
    fitView({ padding: 0.2 })
    setZoomPct(Math.round(getZoom() * 100))
  }, [canvasLocked, fitView, getZoom])

  const handleZoomIn = useCallback(() => {
    if (canvasLocked) return
    zoomIn()
    setZoomPct(Math.round(getZoom() * 100))
  }, [canvasLocked, zoomIn, getZoom])

  const handleZoomOut = useCallback(() => {
    if (canvasLocked) return
    zoomOut()
    setZoomPct(Math.round(getZoom() * 100))
  }, [canvasLocked, zoomOut, getZoom])

  return (
    <div className={styles.canvasWrapper}>
      <CanvasActionsMenu
        hasCanvasNodes={hasCanvasNodes || reduxNodes.length > 0}
        onLockPipeline={handleToggleLock}
        onClearCanvas={() => dispatch(clearCanvasAction())}
        canvasLocked={canvasLocked}
      />

      <div className={styles.canvas}>
        <AdpReactFlowCanvas
          canvasLocked={canvasLocked}
          reduxNodes={decoratedNodes}
          reduxEdges={decoratedEdges}
          onZoomPctChange={setZoomPct}
          onHasCanvasNodesChange={onHasCanvasNodesChange}
        />

        <ValidationIssuesPanel />

        <FloatingToolbar
          zoomPct={zoomPct}
          setZoomPct={setZoomPct}
          canvasLocked={canvasLocked}
          handleFit={handleFit}
          handleUndo={handleUndo}
          handleRedo={handleRedo}
          handleToggleLock={handleToggleLock}
          handleZoomOut={handleZoomOut}
          handleZoomIn={handleZoomIn}
          getViewport={getViewport}
          setViewport={setViewport}
          undoStackLength={canUndo ? 1 : 0}
          redoStackLength={canRedo ? 1 : 0}
        />
      </div>
    </div>
  )
}

// ─── Validation decoration ────────────────────────────────────────────────────
// Joins canvas nodes/edges with the validation result so failing items render
// with error styling. Memoized at the call site so node/edge identity is stable
// across drags (validation only changes on validate-click).

const FAILING_EDGE_STYLE: React.CSSProperties = {
  stroke: '#dc2626',
  strokeWidth: 2,
  strokeDasharray: '6 4',
}

const FAILING_EDGE_MARKER: EdgeMarker = {
  type: MarkerType.ArrowClosed,
  color: '#dc2626',
}

function decorateNodesWithValidation(
  nodes: PipelineNode[],
  result: ValidationResult | null,
  isDirty: boolean
): PipelineNode[] {
  if (!result || isDirty) {
    // Strip any prior decoration once dirty so users aren't misled.
    return nodes.map(node =>
      node.data.validation
        ? ({ ...node, data: { ...node.data, validation: undefined } } as PipelineNode)
        : node
    )
  }

  return nodes.map(node => {
    const issues = result.byNodeId[node.id]
    if (!issues || issues.length === 0) {
      return node.data.validation
        ? ({ ...node, data: { ...node.data, validation: undefined } } as PipelineNode)
        : node
    }
    const hasError = issues.some(i => i.severity === 'error')
    const decoration: NodeValidationDecoration = {
      severity: hasError ? 'error' : 'warning',
      count: issues.length,
      issues,
    }
    return {
      ...node,
      data: { ...node.data, validation: decoration },
    } as PipelineNode
  })
}

function decorateEdgesWithValidation(
  edges: Edge[],
  result: ValidationResult | null,
  isDirty: boolean
): Edge[] {
  if (!result || isDirty) {
    return edges.map(edge =>
      edge.style || edge.animated
        ? { ...edge, style: undefined, animated: false, markerEnd: undefined }
        : edge
    )
  }

  return edges.map(edge => {
    const failed = Boolean(result.byEdgeId[edge.id]?.length)
    if (!failed) {
      return edge.style || edge.animated
        ? { ...edge, style: undefined, animated: false, markerEnd: undefined }
        : edge
    }
    return {
      ...edge,
      style: FAILING_EDGE_STYLE,
      animated: true,
      markerEnd: FAILING_EDGE_MARKER,
    }
  })
}

// Root export
const Canvas = () => (
  <ReactFlowProvider>
    <CanvasFlow />
  </ReactFlowProvider>
)

export default Canvas
